/* eslint-disable @typescript-eslint/no-non-null-assertion */
import { inject, injectable } from 'inversify';
import { TYPES } from '@/engine/ioc/types';
import type { CasePhaseData } from '@/engine/app/CasePhaseData/CasePhaseData.ts';
import type { CaseDataProvider } from '@/engine/app/CaseDataProvider';
import type { IImporter, ImageParams, ImportResult, ImporterConfig, PhaseConfig } from './IImporter';
import { MCSDImporter } from './MCSDImporter';
import { WBPDImporter } from './WBPDImporter';
import type { AlgorithmExtension, ValuesCollection } from '../DataFlow/DataFlow';

export interface IImportDestination {
  registerPhase: (kind: string, id: string, name: string, valuesCollection: ValuesCollection, weight?: number) => void;
  activateAsync: (
    commonValues: ValuesCollection,
    kindToImports: Map<string, Array<string>>,
    algorithmBuilder: AlgorithmExtension,
    algorithmExtensions?: Map<string, AlgorithmExtension>,
    kindToPhaseParams?: Map<string, ImageParams>
  ) => Promise<void>;
}

export interface IDiscrepancyBehavior {
  onDuplicateEntitiesPresent: (
    duplicatesInfo: Map<string, { label: string; duplicates: Array<[string, string]> }>
  ) => void;
  onUnlinkedMeshesPresent: (unlinkedMeshes: Map<string, string>) => void;
  onImagesWithoutObjectsPresent: (imagesWithoutObjects: Map<string, string>) => void;
  onImagesWithoutWeightPresent: (imageKindToPhasesWithoutWeight: Map<string, Map<string, string>>) => void;
  onKindsWithoutImagesPresent: (imageKindsWithoutImages: Map<string, boolean>) => void;
  onPhaseEntitiesDiscrepancy: (absentEntityToNumPhases: Map<string, number>) => void;
  onObligatoryEntitiesMissing: (imageKindToMissingEntities: Map<string, Array<string>>) => void;
  onOptionalEntitiesMissing: (imageKindToMissingEntities: Map<string, Array<string>>) => void;
  onDone?: (
    kindToPhases: Map<string, Array<string>>,
    kindToImports: Map<string, Array<string>>,
    namesResolver: (name: string) => string
  ) => void;
}

class _DiscrepancyBehavior implements IDiscrepancyBehavior {
  public onDuplicateEntitiesPresent(): void {
    //do nothing
  }

  public onUnlinkedMeshesPresent(unlinkedMeshes: Map<string, string>): void {
    if (unlinkedMeshes.size != 0) {
      throw new Error('Import Failed. All meshes must be linked to images.');
    }
  }

  public onImagesWithoutObjectsPresent(): void {
    //do nothing
  }

  public onImagesWithoutWeightPresent(): void {
    //do nothing
  }

  public onKindsWithoutImagesPresent(imageKindsWithoutImages: Map<string, boolean>): void {
    let isError = false;
    for (const [, isOptional] of imageKindsWithoutImages) {
      if (!isOptional && !isError) {
        isError = true;
      }
    }
    if (isError) {
      throw new Error('Import Failed. Obligatory image kind has no images.');
    }
  }

  public onPhaseEntitiesDiscrepancy(): void {
    //do nothing
  }

  public onObligatoryEntitiesMissing(imageKindToMissingEntities: Map<string, Array<string>>): void {
    if (imageKindToMissingEntities.size != 0) {
      throw new Error('Import Failed. Obligatory entities are missing.');
    }
  }

  public onOptionalEntitiesMissing(): void {
    //do nothing
  }
}

export class ImportProcessor {
  private readonly _importers: Array<IImporter>;

  private readonly _destination: IImportDestination;

  private readonly _discrepancyBehavior: IDiscrepancyBehavior;

  public constructor(
    destination: IImportDestination,
    importers: Array<IImporter>,
    discrepancyBehavior?: IDiscrepancyBehavior
  ) {
    this._destination = destination;
    this._importers = importers;
    this._discrepancyBehavior = discrepancyBehavior ?? new _DiscrepancyBehavior();
  }

  public async processImport(config: ImporterConfig): Promise<void> {
    const importResult: ImportResult = {
      phaseData: new Map<string, PhaseConfig>(),
      unlinkedMeshes: new Map<string, string>(),
      imageIdToDuplicateEntities: new Map<string, Array<[string, string]>>(),
      imagesWithoutObjects: new Map<string, string>()
    };
    for (const importer of this._importers) {
      await importer.import(importResult, config);
    }

    if (importResult.unlinkedMeshes.size != 0) {
      this._discrepancyBehavior.onUnlinkedMeshesPresent(importResult.unlinkedMeshes);
    }

    if (importResult.imageIdToDuplicateEntities.size != 0) {
      const duplicatesInfo = new Map<string, { label: string; duplicates: Array<[string, string]> }>();
      for (const [imageId, duplicates] of importResult.imageIdToDuplicateEntities) {
        duplicatesInfo.set(imageId, {
          label: importResult.phaseData.get(imageId)?.images.at(0)?.label ?? '',
          duplicates: duplicates
        });
      }
      this._discrepancyBehavior.onDuplicateEntitiesPresent(duplicatesInfo);
    }

    if (importResult.imagesWithoutObjects.size != 0) {
      this._discrepancyBehavior.onImagesWithoutObjectsPresent(importResult.imagesWithoutObjects);
    }

    const imagesOrder: Map<string, Array<{ key: string; label: string; weight: number | undefined }>> = new Map();
    for (const [key, value] of importResult.phaseData) {
      for (const image of value.images) {
        let order = imagesOrder.get(image.kind);
        if (!order) {
          order = new Array<{ key: string; label: string; weight: number | undefined }>();
          imagesOrder.set(image.kind, order);
        }
        order.push({
          key: key,
          label: image.label,
          weight: config.phaseWeightResolver?.(image.kind, image.label)
        });
      }
    }
    const imageKindToPhasesWithoutWeight = new Map<string, Map<string, string>>();
    const imageKindsWithoutImages = new Map<string, boolean>(); // boolean goes for isOptional flag
    for (const [kind, order] of imagesOrder) {
      if (order.length > 1) {
        // if kind consists of several phases they must be weighted
        const filteredOut = order.filter((val) => val.weight === undefined);
        if (filteredOut.length != 0) {
          imageKindToPhasesWithoutWeight.set(
            kind,
            new Map<string, string>(filteredOut.map((obj) => [obj.key, obj.label]))
          );
          // keep only phases with weight
          order.splice(0, order.length, ...order.filter((val) => val.weight !== undefined));
        }
      }
      if (order.length == 0) {
        imageKindsWithoutImages.set(kind, config.optionalImagePhaseKinds?.includes(kind) === true);
      }
    }
    if (imageKindToPhasesWithoutWeight.size > 0) {
      this._discrepancyBehavior.onImagesWithoutWeightPresent(imageKindToPhasesWithoutWeight);
    }
    if (imageKindsWithoutImages.size > 0) {
      // delete kinds without images
      for (const [kind] of imageKindsWithoutImages) {
        importResult.phaseData.delete(kind);
        imagesOrder.delete(kind);
      }
      this._discrepancyBehavior.onKindsWithoutImagesPresent(imageKindsWithoutImages);
    }

    const allConfigs = Array.from(config.meshes).concat(
      Array.from(config.points),
      Array.from(config.splines),
      Array.from(config.polylinesVectors)
    );

    // gather here per-kind infos for read entities completeness analysis
    const kindToInfo = new Map<
      string, // kind
      {
        phases: Array<string>; // phasesIds of this kind
        availableEntities: {
          all: Set<string>; // all read entities
          obligatory: Set<string>; // obligatory read entities
        };
        missingObligatoryEntities: Array<string>;
      }
    >();
    // gather here all the possible entities for each kind
    const kindToImports = new Map<string, Array<string>>();
    // gather here all the read entities and phases they are present on
    const readEntities = new Map<
      string,
      { phases: Array<string>; phaseKind: string; isOptional: boolean; importName: string }
    >();
    for (const entity of allConfigs) {
      const imagePhaseKind = entity.imagePhaseKind ?? '{unlinked}';
      // 1. fill in kindToImports
      if (!kindToImports.has(imagePhaseKind)) {
        kindToImports.set(imagePhaseKind, []);
      }
      kindToImports.get(imagePhaseKind)!.push(entity.algoName);
      // 2. init kindToInfo
      if (!kindToInfo.has(imagePhaseKind)) {
        kindToInfo.set(imagePhaseKind, {
          phases: [],
          availableEntities: {
            all: new Set(),
            obligatory: new Set()
          },
          missingObligatoryEntities: []
        });
      }
      // 3. init in readEntities
      readEntities.set(entity.algoName, {
        phases: [],
        phaseKind: imagePhaseKind,
        isOptional: entity.isOptionalEntity,
        importName: entity.importName
      });
    }
    for (const [phaseId, phaseConfig] of importResult.phaseData) {
      // 4. fill in kindToInfo.phases
      for (const kind of phaseConfig.imageKinds) {
        kindToInfo.get(kind)?.phases.push(phaseId);
      }
      // 5. fill in readEntities.phases
      for (const name of phaseConfig.valuesCollection.keys()) {
        const info = readEntities.get(name);
        if (!info) throw new Error('Entity config not found'); // check allConfigs contain all the possible entity types
        info.phases.push(phaseId);
      }
    }

    // gather entities present on all the phases of the same kind (multiphase), gather obligatory read entities
    const absentEntityToNumPhases = new Map<string, number>();
    for (const [entityName, info] of readEntities) {
      if (info.phases.length == 0) continue;
      const kindInfo = kindToInfo.get(info.phaseKind);
      if (!kindInfo) throw new Error('Unknown kind');
      // we need only read entities that are present on all phases of the same kind
      if (info.phases.length == kindInfo.phases.length) {
        // if present on all phases
        // 6. fill in kindToInfo.availableEntities
        kindInfo.availableEntities.all.add(entityName);
        if (!info.isOptional) {
          kindInfo.availableEntities.obligatory.add(entityName);
        }
      } else {
        absentEntityToNumPhases.set(info.importName, kindInfo.phases.length - info.phases.length);
      }
    }
    if (absentEntityToNumPhases.size != 0) {
      this._discrepancyBehavior.onPhaseEntitiesDiscrepancy(absentEntityToNumPhases);
    }

    // find missing obligatory entities
    for (const entity of allConfigs) {
      const imagePhaseKind = entity.imagePhaseKind ?? '{unlinked}';
      const kindInfo = kindToInfo.get(imagePhaseKind);
      if (!kindInfo) throw new Error('Unknown kind');
      if (!entity.isOptionalEntity && !kindInfo.availableEntities.obligatory.has(entity.algoName)) {
        // 7. fill in kindToInfo.missingObligatoryEntities
        kindInfo.missingObligatoryEntities.push(entity.importName);
      }
    }
    const imageKindToMissingEntities = new Map<string, Array<string>>();
    for (const [kind, info] of kindToInfo) {
      if (config.optionalImagePhaseKinds?.includes(kind) === true) {
        // For Optional Image Phase kind,
        // ALL obligatory entities must be either unavailable all together or present all together.
        // In case of partial presence - discrepancy.
        if (info.availableEntities.obligatory.size != 0 && info.missingObligatoryEntities.length != 0) {
          imageKindToMissingEntities.set(kind, info.missingObligatoryEntities);
        }
      } else {
        if (info.missingObligatoryEntities.length != 0) {
          imageKindToMissingEntities.set(kind, info.missingObligatoryEntities);
        }
      }
    }
    if (imageKindToMissingEntities.size != 0) {
      this._discrepancyBehavior.onObligatoryEntitiesMissing(imageKindToMissingEntities);
    }

    const kindToPhases = new Map<string, Array<string>>();
    const imageKindToMissingOptionalEntities = new Map<string, Array<string>>();
    for (const [kind, order] of imagesOrder) {
      const kindInfo = kindToInfo.get(kind);
      if (!kindInfo) throw new Error('Unknown kind');
      const kindImports = kindToImports.get(kind);
      if (!kindImports) throw new Error('Unknown kind');
      order.sort((a, b) => (a.weight ?? -1) - (b.weight ?? -1));
      const kindPhases = new Array<string>();
      for (const image of order) {
        const value = importResult.phaseData.get(image.key);
        if (!value) throw new Error('Logic error');
        const valuesCollection = Array.from(value.valuesCollection.entries()).filter(
          (entry) => kindInfo.availableEntities.all.has(entry[0]) // 8. filter out entities present not on all phases
        );
        kindPhases.push(image.label);
        if (kind != '{unlinked}') {
          this._destination.registerPhase(kind, image.key, image.label, valuesCollection, image.weight);
        }
      }
      kindToPhases.set(kind, kindPhases);
      kindToImports.set(
        kind,
        kindImports.filter(
          (entry) => kindInfo.availableEntities.all.has(entry) // 9. filter out entities present not on all phases
        )
      );
      // find missing optional entities
      const missingOptionalEntities = new Array<string>();
      for (const entry of kindImports) {
        if (!kindInfo.availableEntities.all.has(entry)) {
          const importName = readEntities.get(entry)?.importName ?? entry;
          missingOptionalEntities.push(importName);
        }
      }
      if (missingOptionalEntities.length != 0) {
        imageKindToMissingOptionalEntities.set(kind, missingOptionalEntities);
      }
    }
    if (imageKindToMissingOptionalEntities.size != 0) {
      this._discrepancyBehavior.onOptionalEntitiesMissing(imageKindToMissingOptionalEntities);
    }

    this._discrepancyBehavior.onDone?.(
      kindToPhases,
      kindToImports,
      (name) => readEntities.get(name)?.importName ?? name
    );

    const commonValues = Array.from(importResult.phaseData.get('{none}')?.valuesCollection.entries() ?? []);

    await this._destination.activateAsync(
      commonValues,
      kindToImports,
      config.algorithmBuilder,
      config.algorithmExtensions,
      config.imageParams
    );
  }
}

@injectable()
export class ImporterFactory {
  private readonly _caseDataProvider: CaseDataProvider;

  private readonly _casePhaseData: CasePhaseData;

  public constructor(
    @inject(TYPES.CasePhaseData) casePhaseData: CasePhaseData,
    @inject(TYPES.CaseDataProvider) caseDataProvider: CaseDataProvider
  ) {
    this._casePhaseData = casePhaseData;
    this._caseDataProvider = caseDataProvider;
  }

  public create(surgeryType: string): ImportProcessor {
    const importers: Array<IImporter> = [];
    importers.push(new MCSDImporter(() => this._caseDataProvider.getCaseHeader()));

    if (surgeryType === 'LUNG') {
      importers.push(new WBPDImporter(() => this._caseDataProvider.getCaseBlob('WebPlanner.wbpd')));
    }

    return new ImportProcessor(this._casePhaseData, importers);
  }
}
